"use client";
import React, { useState, useCallback } from "react";
import Image, { ImageProps } from "next/image";
import fallbackImg from "public/images/fallback-image.png";

interface ImageWithFallbackProps extends Omit<ImageProps, 'src' | 'alt'> {
  src: string;
  fallbackSrc?: string | typeof fallbackImg;
  /**
   * Descriptive, context-rich alt text for multimodal AI engines (TNA-268 #3).
   * Prefer "Spacious deck of the Blue Ridge cabin with an outdoor hot tub
   * overlooking the mountains at sunset" over "deck_hot_tub". Decorative-only
   * images may pass an empty string intentionally.
   */
  alt?: string;
  priority?: boolean;
  fetchPriority?: "auto" | "high" | "low";
  quality?: number;
  width: number;
  height: number;
}

/**
 * Dev-only guard nudging editors toward descriptive alt text. Flags empty alt
 * and filename-like alt ("deck_hot_tub", "IMG_1234.jpg") that give AI engines
 * no context. Warn-only — never blocks rendering or the build.
 */
function warnOnWeakAltText(alt: string, src: string) {
  if (process.env.NODE_ENV === "production") return;
  const looksLikeFilename = /^[\w-]+(\.(jpe?g|png|webp|avif|gif|svg))?$/i.test(
    alt.trim(),
  );
  if (!alt.trim()) {
    console.warn(
      `[ImageWithFallback] Missing alt text for "${src}". Add descriptive, context-rich alt text for SEO/AI engines (or pass alt="" only if purely decorative).`,
    );
  } else if (alt.trim().length < 15 || looksLikeFilename) {
    console.warn(
      `[ImageWithFallback] Weak alt text "${alt}" for "${src}". Describe the scene in full (e.g. "Spacious deck with hot tub overlooking the mountains").`,
    );
  }
}

export const ImageWithFallback = React.memo<ImageWithFallbackProps>(
  ({
    src,
    fallbackSrc = fallbackImg,
    alt = "",
    priority = false,
    fetchPriority = "auto",
    unoptimized = false,
    // Default to 75 (Next.js default): AVIF/WebP at q75 is visually lossless for
    // photos while being far lighter than q95. Callers can raise it for hero/
    // full-screen imagery where extra sharpness matters.
    quality = 75,
    // Allow an explicit loading override (e.g. eager-loading carousel neighbours
    // for instant switching) without forcing a high-priority preload.
    loading,
    width,
    height,
    ...rest
  }) => {
    const [hasError, setHasError] = useState<boolean>(false);

    warnOnWeakAltText(alt, src);

    const handleError = useCallback(() => {
      if (!hasError && fallbackSrc) {
        setHasError(true);
      }
    }, [fallbackSrc, hasError]);

    return (
      <Image
        {...rest}
        src={hasError ? fallbackSrc : src}
        alt={alt}
        width={width}
        height={height}
        onError={handleError}
        priority={priority}
        loading={loading ?? (priority ? "eager" : "lazy")}
        fetchPriority={priority ? "high" : fetchPriority}
        quality={quality}
        unoptimized={unoptimized}
      />
    );
  }
);

ImageWithFallback.displayName = "ImageWithFallback";

